import { useCallback, useEffect, useState } from "react";
import { NavLink, Route, Routes, useParams } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import SupportConversationPage from "../../common/SupportChat/SupportConversationPage";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import PlatformPageHeader from "./PlatformPageHeader";
import { ReadTicks } from "../../Clinic/common/ChatThread";
import cs from "../../Clinic/common/chat.module.css";
import s from "./platformAdmin.module.css";

function formatPreviewTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function PlatformSupportInbox() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await platformAPI.listSupportThreads();
      setThreads(data.threads || []);
    } catch {
      setThreads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, 3000);
    const onActivity = () => load();
    window.addEventListener("baysport:support-activity", onActivity);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("baysport:support-activity", onActivity);
    };
  }, [load]);

  if (loading) {
    return <p className={s.stateMuted}>Загрузка…</p>;
  }

  if (!threads.length) {
    return (
      <div className={s.supportEmpty}>
        <p className={s.supportEmptyTitle}>Пока нет обращений</p>
        <p className={s.supportEmptyHint}>
          Чаты появятся, когда владелец или пользователь клуба напишет в поддержку.
        </p>
      </div>
    );
  }

  return (
    <ul className={`${cs.chatInboxList} ${s.platformInboxList}`}>
      {threads.map((thread) => (
        <li key={thread.thread_key}>
          <NavLink
            to={`/admin/support/${encodeURIComponent(thread.thread_key)}`}
            className={({ isActive }) =>
              isActive
                ? `${cs.chatInboxRow} ${cs.chatInboxRowActive}`
                : cs.chatInboxRow
            }
          >
            <span className={cs.chatInboxAvatarClub} aria-hidden>
              {thread.channel === "owner" ? "👤" : "🏟"}
            </span>
            <span className={cs.chatInboxBody}>
              <span className={cs.chatInboxTop}>
                <span className={cs.chatInboxName}>{thread.title}</span>
                <span className={cs.chatInboxTime}>
                  {formatPreviewTime(thread.last_at)}
                </span>
              </span>
              <span className={cs.chatInboxPreviewRow}>
                {thread.last_is_mine ? (
                  <ReadTicks read={Boolean(thread.last_is_read)} />
                ) : null}
                <span className={cs.chatInboxPreview}>
                  {thread.subtitle}
                  {thread.last_body ? ` · ${thread.last_body}` : ""}
                </span>
              </span>
            </span>
            {thread.unread_count > 0 ? (
              <span className={cs.chatInboxBadge}>
                {thread.unread_count > 99 ? "99+" : thread.unread_count}
              </span>
            ) : null}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

function PlatformSupportThread() {
  const { threadKey } = useParams();
  const decodedKey = decodeURIComponent(threadKey || "");
  const [meta, setMeta] = useState(null);
  const isMobile = useIsMobile(900);

  if (isMobile) {
    return (
      <SupportConversationPage
        mode="platform"
        threadKey={decodedKey}
        title={meta?.title || "Обращение"}
        subtitle={
          meta?.club_name
            ? `${meta.club_name}${meta.user_role ? ` · ${meta.user_role}` : ""}`
            : meta?.subtitle || "Поддержка"
        }
        fullscreen
        backTo="/admin/support"
        onMeta={setMeta}
      />
    );
  }

  return (
    <div className={s.supportThreadLayout}>
      <div className={s.supportThreadSide}>
        <div className={s.supportThreadSideHead}>
          <span className={s.supportSideTitle}>Обращения</span>
        </div>
        <PlatformSupportInbox />
      </div>
      <div className={s.supportThreadMain}>
        <SupportConversationPage
          mode="platform"
          threadKey={decodedKey}
          title={meta?.title || "Обращение"}
          subtitle={
            meta?.club_name
              ? `${meta.club_name}${meta.user_role ? ` · ${meta.user_role}` : ""}`
              : meta?.subtitle || "Поддержка"
          }
          fullscreen={false}
          hideBack
          onMeta={setMeta}
        />
      </div>
    </div>
  );
}

export default function PlatformSupport() {
  const isMobile = useIsMobile(900);

  return (
    <div className={`${s.supportPage} ${isMobile ? s.supportPageMobile : ""}`}>
      {!isMobile ? (
        <PlatformPageHeader
          title="Поддержка"
          lead="Обращения владельцев и пользователей клубов."
        />
      ) : null}
      <Routes>
        <Route
          index
          element={
            <div className={s.supportInboxOnly}>
              {isMobile ? (
                <header className={s.appHubHead}>
                  <h1 className={s.appHubTitle}>Поддержка</h1>
                </header>
              ) : null}
              <PlatformSupportInbox />
            </div>
          }
        />
        <Route path=":threadKey" element={<PlatformSupportThread />} />
      </Routes>
    </div>
  );
}
